import { Pressable, TextInput, View } from 'dripsy';
import React from 'react';
import { Icon, SVGIconList } from '../../icons/Icon';
import { TextCaption } from '../../typography';
import { InputBorder } from '../InputBorder';
import { INPUT_SIZE, INPUT_STATE, INPUT_VARIANT } from '../types';
import { useInput } from './useInput';

export interface InputProps {
  onValueChange: (value?: string) => void;
  label?: string;
  value?: string;
  defaultValue?: string;
  errorText?: string;
  disabled?: boolean;
  prefixText?: string;
  prefixIcon?: SVGIconList;
  suffixText?: string;
  suffixIcon?: SVGIconList;
  allowClear?: boolean;
  size?: INPUT_SIZE;
  variant?: INPUT_VARIANT;
  placeholder?: string;
}

export const Input = ({
  onValueChange,
  label,
  value,
  defaultValue,
  errorText,
  disabled,
  prefixText,
  prefixIcon,
  suffixText,
  suffixIcon,
  allowClear,
  size,
  variant = INPUT_VARIANT.ROUNDED,
  placeholder
}: InputProps) => {
  const controller = useInput({
    value,
    defaultValue,
    errorText,
    disabled,
    onValueChange
  });

  return (
    <View>
      {/* LABEL */}
      {!!label && (
        <React.Fragment>
          <TextCaption semiBold>{label}</TextCaption>
          <View sx={{ height: '$1' }} />
        </React.Fragment>
      )}
      {/* INPUT */}
      <InputBorder variant={variant} state={controller.inputState} size={size} onPress={controller.onFocus}>
        {/* PREFIX */}
        {!!prefixText && (
          <TextCaption semiBold sx={{ mt: 2 }}>
            {prefixText}
          </TextCaption>
        )}
        {!!prefixIcon && <Icon name={prefixIcon} />}
        {(prefixText || prefixIcon) && <View sx={{ width: '$2' }} />}
        {/* INPUT */}
        <TextInput
          ref={controller.inputRef}
          value={controller.value}
          defaultValue={defaultValue}
          onChangeText={controller.onChange}
          placeholder={placeholder}
          onFocus={controller.onFocus}
          onBlur={controller.onBlur}
          sx={{
            width: '100%',
            outline: 'none',
            outlineStyle: 'none',
            backgroundColor: 'transparent'
          }}
          editable={!disabled}
        />
        {/* SUFFIX */}
        {(suffixText || suffixIcon) && <View sx={{ width: '$2' }} />}
        {!!suffixText && (
          <TextCaption semiBold sx={{ mt: 2 }}>
            {suffixText}
          </TextCaption>
        )}
        {!!suffixIcon && <Icon name={suffixIcon} />}
        {/* ALLOW CLEAR */}
        {allowClear && (
          <Pressable onPress={controller.onClear}>
            <Icon name="clearText" />
          </Pressable>
        )}
      </InputBorder>
      {/* ERROR TEXT */}
      {!!errorText && (
        <React.Fragment>
          <View sx={{ height: '$1' }} />
          <TextCaption sx={{ color: '$danger' }}>{errorText}</TextCaption>
        </React.Fragment>
      )}
    </View>
  );
};
